import { CATEGORY_COLOR, categoryOf } from '@trips/shared/categories';
import { flightStatus } from '@trips/shared/flight-status';
import { flightMilestones } from '@trips/shared/flight-times';
import { type FormatPrefs, formatDuration, formatTime } from '@trips/shared/format';
import { flightLabel, ITEM_TYPE_LABELS, type PlaceLookup } from '@trips/shared/items';
import type { Item } from '@trips/shared/schema';
import type { DayRow, EventKind, EventRow, TimelineModel } from '@trips/shared/timeline';
import { type CSSProperties, memo, useEffect, useMemo } from 'react';
import { selectPlaceLookup, selectPrefs } from '../../data/selectors';
import { useStore } from '../../data/store';
import { now } from '../../lib/clock';
import { phoneLink } from '../../lib/link';
import { navigate } from '../../lib/router';
import { Icon } from '../../ui/Icon';
import { itemIcon } from '../../ui/itemIcon';
import { StatusPill } from '../../ui/StatusPill';
import s from './Timeline.module.css';

function openRow(row: EventRow, tripId: string) {
  if (row.itemType === 'flight') navigate(`/flights/${row.itemId}`);
  else navigate(`/trips/${tripId}/plans/${row.itemId}`, { overlay: true });
}

const EVENT_LABEL: Partial<Record<EventKind, string>> = {
  depart: 'Depart',
  arrive: 'Arrive',
  checkin: 'Check in',
  checkout: 'Check out',
  pickup: 'Pick up',
  dropoff: 'Drop off',
};

/** The World App's color for this kind of plan, as the card's tint. */
const tint = (type: Item['type']) =>
  ({ '--kind': CATEGORY_COLOR[categoryOf(type)] }) as CSSProperties;

/** The card's name and at most three facts worth a glance; everything else is a tap away. */
function content(
  row: EventRow,
  item: Item | undefined,
  places: PlaceLookup,
  prefs: FormatPrefs,
): { title: string; chips: string[] } {
  const firstDetail = row.subtitle?.split(', ')[0] ?? null;
  if (row.cancelled) return { title: row.title, chips: ['Cancelled'] };
  if (item?.details.type === 'flight') {
    const d = item.details;
    const at = (id: string | null) => {
      const p = places(id);
      return p ? [p.city, p.iata].filter(Boolean).join(' ') : null;
    };
    if (row.event === 'depart') {
      const chips = [at(item.startPlaceId) ?? d.origin];
      if (item.startAt != null) {
        const boarding = flightMilestones({
          scheduledDepart: item.startAt,
          airline: d.airline,
          international: false,
          boardingAt: d.boardingAt,
        }).find((m) => m.key === 'boarding');
        if (boarding) chips.push(`Boards ${formatTime(boarding.at, item.startTz ?? 'UTC', prefs)}`);
      }
      if (d.terminal) chips.push(`Terminal ${d.terminal}`);
      if (d.seat) chips.push(`Seat ${d.seat}`);
      return { title: flightLabel(item), chips: chips.slice(0, 3) };
    }
    return { title: flightLabel(item), chips: [at(item.endPlaceId) ?? d.destination] };
  }
  const stay = /^(Check in|Check out), (.+)$/.exec(row.title);
  if (stay)
    return {
      title: stay[2]!,
      chips: stay[1] === 'Check in' && firstDetail ? [firstDetail] : [],
    };
  if (item?.type === 'car')
    return { title: item.provider ?? row.title, chips: firstDetail ? [firstDetail] : [] };
  return { title: row.title, chips: firstDetail ? [firstDetail] : [] };
}

const Event = memo(function Event({ row, tripId }: { row: EventRow; tripId: string }) {
  const item = useStore((st) => st.tables.items.get(row.itemId));
  const prefs = useStore(selectPrefs);
  const places = useStore(selectPlaceLookup);
  const live = useStore((st) => st.tables.flightLive.get(row.itemId));
  const { title, chips } = content(row, item, places, prefs);
  const label = EVENT_LABEL[row.event] ?? ITEM_TYPE_LABELS[row.itemType];
  // Delays the way byAir shows them: the new time, the old one struck through, and a status.
  const t = now();
  const flight =
    item?.type === 'flight' &&
    item.startAt != null &&
    (row.event === 'depart' || row.event === 'arrive')
      ? item
      : null;
  const fs =
    flight &&
    (live || flight.status === 'cancelled') &&
    flight.startAt! - t < 48 * 3_600_000 &&
    (flight.endAt ?? flight.startAt!) + 6 * 3_600_000 > t
      ? flightStatus(
          {
            startAt: flight.startAt!,
            endAt: flight.endAt,
            cancelled: flight.status === 'cancelled',
          },
          live,
          t,
          undefined,
          phoneLink(),
        )
      : null;
  const pair = fs ? (row.event === 'depart' ? fs.depart : fs.arrive) : null;
  const pairTz = row.event === 'depart' ? flight?.startTz : (flight?.endTz ?? flight?.startTz);
  return (
    <button
      type="button"
      className={`${s.card} ${row.cancelled ? s.cancelled : ''}`}
      style={tint(row.itemType)}
      data-row={row.key}
      onClick={() => openRow(row, tripId)}
    >
      <span className={s.top}>
        <span className={s.badge}>
          <Icon name={itemIcon(item, row.itemType)} size={14} />
          {label}
        </span>
        {row.time && (
          <span className={`${s.time} num`}>
            {pair?.moved ? formatTime(pair.best, pairTz ?? 'UTC', prefs) : row.time}
            {pair?.moved && <s className={s.was}>{row.time}</s>}
            {row.tzLabel && <small>{row.tzLabel}</small>}
          </span>
        )}
        {fs && row.event === 'depart' && <StatusPill label={fs.label} tone={fs.tone} />}
      </span>
      <span className={s.title}>{title}</span>
      {chips.length > 0 && (
        <span className={s.chips}>
          {chips.map((c) => (
            <span key={c} className={`${s.chip} num`}>
              {c}
            </span>
          ))}
        </span>
      )}
    </button>
  );
});

/** "Flight 3 h 27 min" between a departure and its arrival. */
function Between({ item }: { item: Item | undefined }) {
  if (!item || item.startAt == null || item.endAt == null) return null;
  const what = item.type === 'flight' ? 'Flight' : 'Duration';
  return (
    <span className={`${s.between} num`}>
      {what} {formatDuration((item.endAt - item.startAt) / 60_000)}
    </span>
  );
}

function DayEvents({ events, tripId }: { events: EventRow[]; tripId: string }) {
  const items = useStore((st) => st.tables.items);
  return (
    <div className={s.events}>
      {events.map((r, i) => {
        const next = events[i + 1];
        const pairs = r.event === 'depart' && next?.itemId === r.itemId && next.event === 'arrive';
        return (
          <div key={r.key} className={s.slot}>
            <Event row={r} tripId={tripId} />
            {pairs && <Between item={items.get(r.itemId)} />}
          </div>
        );
      })}
    </div>
  );
}

interface Day {
  head: DayRow;
  events: EventRow[];
}

export function Timeline({
  model,
  tripId,
  stickyTop,
  unscheduled,
}: {
  model: TimelineModel;
  tripId: string;
  stickyTop: string;
  unscheduled: Item[];
}) {
  // Days that have plans; quiet days between them are left out.
  const days = useMemo(() => {
    const out: Day[] = [];
    for (const r of model.rows) {
      if (r.kind === 'day') out.push({ head: r, events: [] });
      else if (r.kind === 'event') out[out.length - 1]?.events.push(r);
    }
    return out.filter((d) => d.events.length > 0);
  }, [model]);

  // Opening a trip that's underway lands on what's happening now: the latest plan that has
  // started, with what's next below it. Coming back to the trip keeps where I was instead.
  const current = useMemo(() => {
    const t = now();
    let last: string | null = null;
    let ahead = false;
    for (const r of model.rows) {
      if (r.kind !== 'event' || r.at == null) continue;
      if (r.at <= t) last = r.key;
      else ahead = true;
    }
    return last && ahead ? last : null;
  }, [model]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: once per trip, not on every edit
  useEffect(() => {
    if (!current) return;
    const scroller = document.scrollingElement;
    let frame = requestAnimationFrame(() => {
      const el = document.querySelector<HTMLElement>(`[data-row="${CSS.escape(current)}"]`);
      const pane = el?.closest<HTMLElement>('[data-scroll]');
      if (!el || (pane ? pane.scrollTop : (scroller?.scrollTop ?? 0)) > 0) return;
      el.scrollIntoView({ block: 'start' });
      // Days above render at their real height once scrolled past; settle onto the card.
      frame = requestAnimationFrame(() => el.scrollIntoView({ block: 'start' }));
    });
    return () => cancelAnimationFrame(frame);
  }, [tripId]);

  return (
    <div className={s.timeline} style={{ '--sticky-top': stickyTop } as CSSProperties}>
      {days.map(({ head, events }) => (
        <section key={head.key} className={s.day} id={head.isToday ? 'today' : undefined}>
          <h3 className={s.dayHead} style={{ top: stickyTop }}>
            <span>{head.label}</span>
            <span className={head.isToday ? s.today : s.dayNumber}>
              {head.isToday ? 'Today' : `Day ${head.dayNumber}`}
            </span>
          </h3>
          <DayEvents events={events} tripId={tripId} />
        </section>
      ))}
      {unscheduled.length > 0 && (
        <section className={s.day}>
          <h3 className={s.dayHead} style={{ top: stickyTop }}>
            <span>No date yet</span>
          </h3>
          <div className={s.events}>
            {unscheduled.map((i) => (
              <button
                key={i.id}
                type="button"
                className={s.card}
                style={tint(i.type)}
                onClick={() => navigate(`/trips/${tripId}/plans/${i.id}`, { overlay: true })}
              >
                <span className={s.top}>
                  <span className={s.badge}>
                    <Icon name={itemIcon(i, i.type)} size={14} />
                    {ITEM_TYPE_LABELS[i.type]}
                  </span>
                </span>
                <span className={s.title}>{i.title}</span>
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
